<script setup lang="ts">
import { useMemberStats } from '@/composables/poolMetrics/usePoolMetrics'
import { TRIAL_MEMBERSHIP_CAP } from '#shared/constants/foundation'
import { TIER_DEPOSITS } from '#shared/constants/vaultRules'
import { LANDING_JOIN } from '@/content/landing'

// Shares the page's member-stats fetch, so this adds no request.
const { data: stats } = useMemberStats({ lazy: true })

/** "$100, $500 or $1,000", from the tier minimums rather than typed out. */
const depositAmounts = new Intl.ListFormat('en', { type: 'disjunction' }).format(
  [TIER_DEPOSITS.bronze, TIER_DEPOSITS.silver, TIER_DEPOSITS.gold].map(amount => formatCurrency(amount, { decimals: 0 })),
)

const steps = LANDING_JOIN.steps.map(step => ({
  ...step,
  body: step.body.replace('{amounts}', depositAmounts),
}))

/**
 * The $0 route, offered only while trial spots are open. `null` until the member counts
 * load, and when the trial is full, so the offer never promises a spot that isn't there.
 */
const trialOffer = computed(() => {
  if (stats.value == null)
    return null
  const remaining = Math.max(TRIAL_MEMBERSHIP_CAP - (stats.value.totalByTier?.trial ?? 0), 0)
  if (remaining === 0)
    return null
  return LANDING_JOIN.trialOffer.label
    .replace('{remaining}', String(remaining))
    .replace('{spots}', remaining === 1 ? LANDING_JOIN.trialOffer.spot : LANDING_JOIN.trialOffer.spots)
})
</script>

<template>
  <UISection :level="2" band="how" :kicker="LANDING_JOIN.kicker" :title="LANDING_JOIN.heading">
    <!-- A connected progression: each step's line fills toward the next as the section appears. -->
    <ol class="join-steps">
      <li
        v-for="(step, idx) in steps"
        :key="step.id"
        class="join-step"
        :style="{ '--i': idx }"
      >
        <div class="join-step__marker" aria-hidden="true">
          <span class="join-step__node">
            <UIIcon :name="step.icon" size="md" />
          </span>
        </div>

        <div class="min-w-0">
          <UIText variant="label">
            {{ LANDING_JOIN.stepLabel.replace('{n}', String(idx + 1)) }}
          </UIText>
          <h3 class="mt-1 font-heading text-lg font-bold tracking-tight text-ink">
            {{ step.title }}
          </h3>
          <p class="mt-1.5 text-[0.92rem]/relaxed text-ink-soft">
            {{ step.body }}
          </p>

          <ul v-if="step.documents" class="mt-3 flex flex-wrap gap-1.5">
            <li
              v-for="doc in step.documents"
              :key="doc"
              class="join-doc"
            >
              {{ doc }}
            </li>
          </ul>

          <NuxtLink
            v-if="step.id === 'deposit' && trialOffer"
            :to="LANDING_JOIN.trialOffer.href"
            class="join-trial mt-3"
          >
            {{ trialOffer }}
            <span aria-hidden="true">→</span>
          </NuxtLink>
        </div>
      </li>
    </ol>
  </UISection>
</template>
